<template>
    <guides-conversational-ai-container
        v-if="localGuide && shouldShow"
        :id="conversationId"
        :themes="themesOptions"
        :theme="selectedTheme"
        :app-id="parsedAppId"
        :conversation="conversation"
        :is-loading="isLoading"
        :generated-guide-name="localGuide.name"
        :steps="localGuide.steps"
        :save-disabled="saveDisabled"
        :error-message="errorMessage"
        :guide-id="guideId"
        :guide="localGuide"
        :guides="guides"
        :turndown-service="turndownService"
        :is-mobile="isMobile"
        :all-steps-have-same-theme="allStepsHaveSameTheme"
        :segment="segment"
        :should-show-llm-undo="shouldShowLlmUndo"
        :agent-id="agentId"
        :is-from-agent-mode="isFromAgentMode"
        :show-chat="showChat"
        @create-guide="saveStepsToGuide"
        @add-step="addStep"
        @copy-step="copyStep"
        @delete-step="deleteStep"
        @send-message="sendMessage"
        @exit="backToRoutes"
        @stop="handleStop"
        @on-theme-change="handleThemeChange"
        @content-block-update="handleContentBlockUpdate"
        @undo-llm-change="handleUndoLlmChange" />
</template>
<script>
import { canUseConversationalAI } from '../helpers/common.util';
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex/dist/vuex.cjs.js';
import GuidesConversationalAiContainer from './guides-conversational-ai-container';
import { BuildingBlock, BuildingBlockMigrations, AiBlocks, configureEmbed, Polls } from '@pendo/services/BuildingBlocks';
import { EDITOR_TYPES } from '@pendo/services/Constants';
import { createGuide, initializeStepsForEditing } from '@/guides/helpers/guide-editor.util.js';
import moment from '@utils/moment-wrapper';
import { getAskPendoPostRequest } from '@/vuex/modules/ask-pendo/ask-pendo.utils';
import { aiTemplatesMap, TEMPLATE_TYPES } from '@/guides/helpers/templates/ai/index';
import { getBlockContent, setBlockContent } from '@/guides/helpers/templates/utils/ai-editor-utils';
import { createArtifactStepsFromStep, makeConvoUiStepKey } from '@/guides/helpers/templates/utils/ai-template-utils';
import { initTurndownService } from './turndown';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { configurePollsGuide, getContent, isEmbeddedGuide } from '@/guides/helpers/common.util';
import { loadGuidesForTaskList } from '@/vuex/modules/guides/guides.utils.js';
import { POLL_ID_ATTRIBUTE } from './editable-elements.utils';
import { PendoNotification } from '@pendo/components';
import { isMobileApp } from '@/utils/apps';
import cloneDeep from 'lodash/cloneDeep';
import { copyStepObject } from '../helpers/guide-editor.util';
import { trackAgentEvent, guidesAgentId } from '@/ask-pendo/utils/agent-analytics';
import { showGuideSavedNotification } from '@/ask-pendo/utils/ask-pendo-artifact.utils';
import { $can } from '@pendo/permissions';

export default {
    name: 'GuidesConversationalAiPage',
    components: {
        GuidesConversationalAiContainer,
    },
    props: {
        template: {
            type: String,
            default: '',
        },
        appId: {
            type: String,
            required: true,
        },
        conversationId: {
            type: String,
            required: true,
        },
        guide: {
            type: Object,
            default: () => null,
        },
        guideId: {
            type: String,
            default: '',
        },
        experimentId: {
            type: String,
            default: '',
        },
        experimentVariant: {
            type: String,
            default: '',
        },
        isModal: {
            type: Boolean,
            default: false,
        },
        isFromAgentMode: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['exit'],
    data () {
        return {

            shouldShow: false,
            isSavingGuide: false,
            errorMessage: null,
            isLoading: false,
            conversation: null,
            generatedTemplateType: '',
            selectedTheme: null,
            guides: [],
            localGuide: null,
            turndownService: null,
            segment: null,
            shouldShowLlmUndo: false,
        };
    },
    computed: {
        ...mapGetters({
            getAppById: 'apps/getAppById',
            activeOrganization: 'organization/getActive',
            isTraining: 'subscription/isTrainingSubscription',
            getGuideById: 'guides/getGuideById',
            hasSegmentFlag: 'auth/hasSegmentFlag',
            hasProductLinePurchased: 'productLines/hasProductLinePurchased',
            maxPriority: 'guides/maxPriority',
            getFeatureFlag: 'subscription/getFeatureFlag',
            getThemesForAppId: 'themes/listForAppId',
            getOrgThemesForAppId: 'themes/getOrgThemesForAppId',
            getDefaultThemeForAppId: 'themes/getDefaultThemeForAppId',
        }),
        ...mapState({
            describeYourUseCase: (state) => state.guides.describeYourUseCase,
            map: (state) => state.guides.map,
        }),
        agentId () {
            return guidesAgentId();
        },
        orgId () {
            return get(this.activeOrganization, 'id', null);
        },
        showChat () {
            const segmentFlag = this.hasSegmentFlag('guideConversationalAI');
            const productLineMetadata = this.hasProductLinePurchased('guides', 'aIGuideContentEnabled');
            const consentFlag = this.getFeatureFlag('enableGenerateAIGuideContent');
            const hasAiGuideCreationAccess = canUseConversationalAI(segmentFlag, productLineMetadata, consentFlag);
            const userHasCreateGuideFromScratchPermission = $can(this.parsedAppId)
                .entity('guide')
                .action('create')
                .source('scratch')
                .target('ANY');

            return hasAiGuideCreationAccess && userHasCreateGuideFromScratchPermission;

        },
        orgResourcesEnabled () {
            return get(this.activeOrganization, 'organizationFlags.enableResourceSharing', false);
        },
        orgThemes () {
            return this.getOrgThemesForAppId(this.parsedAppId);
        },
        themesOptions () {
            const appThemes = this.getThemesForAppId(this.parsedAppId);

            if (this.orgResourcesEnabled && this.orgThemes.length > 0) {
                return [
                    {
                        label: 'Organization',
                        id: this.orgId,
                        options: this.orgThemes,
                    },
                    {
                        label: 'Subscription',
                        id: this.parsedAppId,
                        options: appThemes,
                    },
                ];
            }

            return appThemes;
        },
        parsedAppId () {
            return parseInt(this.appId);
        },
        saveDisabled () {
            return !this.localGuide.steps.length || this.localGuide.steps.length === 0 || this.isSavingGuide;
        },
        isEmbed () {
            return isEmbeddedGuide(this.localGuide);
        },
        templateMessages () {
            return {
                announcement: this.$t('guidesConversationalAiPage.template.announcementMessage'),
                mobileAnnouncement: this.$t('guidesConversationalAiPage.template.announcementMessage'),
                embeddedBanner: this.$t('guidesConversationalAiPage.template.embeddedBannerMessage'),
                walkthrough: this.$t('guidesConversationalAiPage.template.walkthroughMessage'),
            };
        },
        app () {
            return this.getAppById(this.parsedAppId);
        },
        platform () {
            return this.app.platform;
        },
        isMobile () {
            return isMobileApp(this.app.platform);
        },
        allStepsHaveSameTheme () {
            const themeIds = this.localGuide.steps.map((step) => step?.attributes?.themeId);

            return themeIds.every((id) => id === themeIds[0]);
        },
        hasUndoableVisualChanges () {
            const { artifact, prevArtifact } = this.conversation;
            const currentSteps = artifact?.steps?.length || 0;
            const prevSteps = prevArtifact?.steps?.length || 0;

            return !(currentSteps === 0 && prevSteps === 0);
        },
    },
    async created () {
        this.turndownService = initTurndownService();
        this.generatedTemplateType = this.template;

        await this.loadGuide({ id: this.guideId, setActive: true, reFetch: false });
        await this.getThemeList();

        this.localGuide = cloneDeep(this.getGuideById(this.guideId)) || cloneDeep(this.guide);

        if (this.localGuide) {
            await this.setupGuideEditingExperience();
        } else {
            await this.setupTemplateExperience();
        }
        this.shouldShow = true;
    },
    methods: {
        ...mapActions({
            getThemeList: 'themes/loadAll',
            loadGuide: 'guides/loadOne',
            patchGuideAndSteps: 'guides/patchGuideAndSteps',
            saveGuide: 'guides/saveGuide',
            updateExperimentVariant: 'experiments/updateVariant',
        }),
        ...mapMutations({
            setDescribeYourUseCase: 'guides/setDescribeYourUseCase',
        }),
        getLayoutTypeForExistingGuide () {
            if (this.localGuide?.layoutType) {
                return this.localGuide.layoutType;
            }

            if (this.isMobile) return TEMPLATE_TYPES.MOBILE_ANNOUNCEMENT;
            if (this.isEmbed) return TEMPLATE_TYPES.EMBEDDED;

            return this.localGuide?.steps?.length === 1 ? TEMPLATE_TYPES.ANNOUNCEMENT : TEMPLATE_TYPES.WALKTHROUGH;
        },
        setSelectedTheme () {
            const guideThemeId = this.localGuide?.steps?.[0]?.attributes?.themeId;
            const hasOrgThemes = this.orgThemes?.length > 0;

            const allThemes = hasOrgThemes
                ? this.themesOptions.flatMap((group) => group.options)
                : this.themesOptions;

            this.selectedTheme = allThemes.find((theme) => theme.id === guideThemeId);
        },
        addStep ({ steps, index }) {
            this.localGuide.steps.splice(index, 0, ...steps);
            initializeStepsForEditing(this.localGuide.steps, this.selectedTheme);
            const aiSteps = steps.map((step) => createArtifactStepsFromStep(step));
            this.conversation.artifact.steps.splice(index, 0, ...aiSteps);
        },
        handleThemeChange (theme) {
            const { id: themeId } = theme;
            this.selectedTheme = theme;
            if (this.localGuide.steps.length > 0) {
                this.localGuide.steps = this.localGuide.steps.map((step) => {
                    BuildingBlock.applyThemeToBuildingBlocks({
                        theme,
                        buildingBlocks: step.buildingBlocks,
                        options: { isEmbed: this.isEmbed },
                    });
                    const reThemedStepDom = JSON.stringify(BuildingBlock.buildingBlocksToDom(step.buildingBlocks));

                    const keyWithUpdatedThemeId = step.key.substring(0, step.key.lastIndexOf('-') + 1) + themeId;

                    return {
                        ...step,
                        attributes: { ...step.attributes, themeId },
                        key: keyWithUpdatedThemeId,
                        dom: reThemedStepDom,
                    };
                });
            }
        },
        backToRoutes () {

            if (this.isModal) {
                this.$emit('exit');

                return;
            }

            if (this.guideId) {
                this.$router.push({
                    name: 'GuideDetails',
                    params: { guideId: this.guideId },
                });
            } else if (this.experimentId) {
                this.$router.push({
                    name: 'ExperimentDetails',
                    params: { id: this.experimentId },
                    query: { openChooser: true, variantId: this.experimentVariant },
                });
            } else {
                this.$router.push({
                    name: 'Guides',
                    query: { openChooser: 'true' },
                });
            }
        },
        async setupGuideEditingExperience () {
            this.setSelectedTheme();
            this.startConversation();

            await getContent(this.localGuide);
            this.localGuides = await loadGuidesForTaskList(this.localGuide, this.map);
            initializeStepsForEditing(this.localGuide.steps, this.selectedTheme);
            this.conversation = this.createNonTemplateConversation();
        },
        getInitialMessageForConversation () {
            if (this.isFromAgentMode) {
                return this.$t('guidesConversationalAiPage.agentModeInitialMessage');
            }

            return this.$t('guidesConversationalAiPage.has_existing_guide');
        },
        createNonTemplateConversation () {
            return {
                ...this.conversation,
                use_provided_artifact: true,
                artifact: {
                    guideName: this.localGuide.name,
                    layoutType: this.getLayoutTypeForExistingGuide(),
                    steps: this.localGuide.steps.map((step) => createArtifactStepsFromStep(step)),
                },
                messages: [
                    {
                        type: 'assistant',
                        content: this.getInitialMessageForConversation(),
                        timestamp: moment().toISOString(),
                    },
                ],
            };
        },
        async setupTemplateExperience () {
            this.localGuide = await createGuide({
                guideType: 'building-block',
                app: { id: this.parsedAppId },
                isMobile: this.isMobile,
                maxPriority: this.maxPriority,
                skipAddStep: true,
                isTraining: this.isTraining,
                editorType: EDITOR_TYPES.VDS,
                launchMethod: 'auto',
                name: `Unnamed ${moment().format('M/D [@] h:mm A')}`,
            });

            if (this.generatedTemplateType.includes('embedded')) {
                this.localGuide = configureEmbed(this.localGuide);
            }

            this.selectedTheme = this.getDefaultThemeForAppId(this.parsedAppId);
            this.startConversation();
        },
        startConversation () {
            if (this.template && !this.describeYourUseCase) {
                this.startTemplateConversation();

                return;
            }

            this.startUseCaseConversation();
        },
        getMessageForTemplate (template) {
            return this.templateMessages[template] || this.$t("guidesConversationalAiPage.Let's build a guide");
        },
        startTemplateConversation () {
            let { options, ...rest } = this.$route.query;
            this.$router.replace({
                path: this.$route.path,
                query: rest,
            });

            if (options) {
                options = JSON.parse(decodeURIComponent(options));
                this.segment = options?.segment;
            }
            const message = options?.initialMessage || this.getMessageForTemplate(this.template);
            const template = aiTemplatesMap[this.template];
            const artifactSteps = template.createArtifactSteps();

            this.localGuide.steps = template.getGeneratedTemplateBlocks({
                theme: this.selectedTheme,
                generatedSteps: artifactSteps,
                steps: this.localGuide.steps,
                platform: this.platform,
            });

            initializeStepsForEditing(this.localGuide.steps, this.selectedTheme);

            const sampleArtifactSteps = this.localGuide.steps.map((step) => createArtifactStepsFromStep(step));

            this.conversation = {
                conversationId: this.conversationId,
                use_provided_artifact: true,
                artifact: {
                    guideName: this.localGuide.name,
                    steps: sampleArtifactSteps,
                    layoutType: this.template,
                },
                messages: [
                    {
                        type: 'assistant',
                        content: `${message}`,
                        timestamp: moment().toISOString(),
                    },
                ],
            };

            if (options?.initialMessage) {
                this.sendConversation();
            }
        },
        startUseCaseConversation () {
            this.conversation = {
                conversationId: this.conversationId,
                artifact: {},
                use_provided_artifact: true,
                messages: [
                    {
                        type: 'assistant',
                        content: this.$t("guidesConversationalAiPage.Let's build a guide"),
                        timestamp: moment().toISOString(),
                    },
                ],
            };

            if (this.describeYourUseCase) {
                const userMessage = {
                    type: 'user',
                    content: this.describeYourUseCase,
                    timestamp: moment().toISOString(),
                };

                if (this.template) {
                    this.conversation.artifact.layoutType = this.template;
                }

                this.conversation.messages.push(userMessage);
                this.setDescribeYourUseCase({ describeYourUseCase: '' });
                this.sendConversation();
            }
        },
        async saveStepsToGuide () {

            if (this.isSavingGuide) return;
            this.isSavingGuide = true;

            const isExistingGuide = this.getGuideById(this.localGuide.id);

            if (!isExistingGuide) {
                this.localGuide.attributes.createdByConversationalAi = true;
            }

            this.localGuide.steps.forEach((step, index) => {
                step.dom = JSON.stringify(BuildingBlock.buildingBlocksToDom(step.buildingBlocks));
                step.buildingBlocks = isExistingGuide ? JSON.stringify(step.buildingBlocks) : step.buildingBlocks;
                step.attributes.themeId = step.attributes.themeId || this.selectedTheme?.id;
                step.guideId = this.localGuide.id;
                step.rank = index;
            });

            Polls.syncPollsForGuide(this.localGuide);

            if (isExistingGuide) {
                await this.patchGuideAndSteps({
                    guide: this.localGuide,
                    stepFieldsToPatch: ['buildingBlocks', 'dom', 'attributes', 'rank'],
                });

                await this.loadGuide({ id: this.localGuide.id, setActive: true, reFetch: true });
            }
            else {
                if (this.segment) {
                    this.localGuide.audienceUiHint = {
                        filters: [{ segmentId: this.segment.id, kind: 'visitor' }],
                    };
                }

                BuildingBlockMigrations.migrate(this.localGuide, 'guide', {});
                BuildingBlockMigrations.migrate(this.localGuide, 'guidesUnversioned');

                await this.saveGuide({ guide: this.localGuide });
                if (this.experimentId && this.experimentVariant) {
                    try {
                        await this.updateExperimentVariant({
                            id: this.experimentId,
                            variant: this.experimentVariant,
                            guide: this.localGuide,
                        });
                    } catch {
                        PendoNotification({
                            type: 'error',
                            duration: 4000,
                            title: this.$t('guides.experiments.errors.variantUpdateFailed', { name: this.localGuide.name }),
                        });
                    }
                }
            }

            if (this.isModal) {
                showGuideSavedNotification({
                    guideId: this.localGuide.id,
                    router: this.$router,
                    title: this.$t('guidesConversationalAiPage.notifications.title.changesSaved'),
                    linkText: this.$t('guidesConversationalAiPage.notifications.message.viewGuide'),
                });

                this.$emit('exit');

                return;
            }

            this.$router.push({
                name: 'GuideDetails',
                params: { guideId: this.localGuide.id },
            });

        },
        handleStop () {
            this.isLoading = false;
        },
        addMessage (message, artifact) {
            const { conversationId, type, content } = message;

            this.conversation.messages.push({
                conversationId,
                type,
                content,
                timestamp: moment().toISOString(),
            });

            if (artifact) {
                this.conversation.artifact = artifact;
            }
        },
        processArtifact (artifact) {
            if (!artifact) return;
            this.conversation.artifact = artifact;

            const { guideName, layoutType, steps } = artifact;

            this.localGuide.name = guideName || this.$t('guidesConversationalAiPage.Unnamed Guide');

            const didLayoutChange = this.generatedTemplateType !== layoutType;
            if (didLayoutChange) {
                this.generatedTemplateType = layoutType;
            }

            if (steps && steps.length > 0) {
                const response = AiBlocks.updateGuideFromArtifact(artifact, this.localGuide.steps, {
                    theme: this.selectedTheme,
                    platform: this.platform,
                    isEmbed: this.isEmbed,
                });

                this.localGuide.steps = [...response];
            } else {
                this.localGuide.steps = [];
                this.generatedTemplateType = this.template;
            }
        },
        processConversationResponse (response) {
            const { artifact, messages } = response;
            const latestResponse = messages[messages.length - 1];
            this.addMessage(latestResponse);
            this.trackAgent('agent_response', {
                conversationId: this.conversationId,
                messageId: `agent_response_${Date.now()}`,
                content: latestResponse.content,
            });

            if (!isEqual(artifact, this.conversation.artifact)) {
                this.conversation.prevArtifact = this.conversation.artifact;
                this.conversation.artifact = artifact;
                this.shouldShowLlmUndo = this.hasUndoableVisualChanges;
                this.processArtifact(artifact);
            }
        },
        async sendConversation () {
            const postRequest = getAskPendoPostRequest('guides');

            this.isLoading = true;
            const lastMessage = this.conversation.messages[this.conversation.messages.length - 1];

            this.trackAgent('prompt', {
                conversationId: this.conversationId,
                messageId: `prompt_${Date.now()}`,
                content: lastMessage?.content,
            });

            try {
                const data = await postRequest(this.conversation);
                if (data.body.error) {
                    throw new Error(data.body.error);
                }

                this.processConversationResponse(data.body);

            } catch (error) {
                throw new Error(
                    `An error occurred while generating the response. Try opening a new conversation.\n\nError: ${error.message}`,
                );
            }
            finally {
                this.isLoading = false;
            }
        },
        async sendMessage (message, artifact) {
            this.errorMessage = null;
            this.addMessage(message, artifact);

            try {
                await this.sendConversation();
            } catch (error) {
                this.errorMessage =
                    error.message ||
                    this.$t(
                        'guidesConversationalAiPage.errorMessages.An error occurred while loading the conversation.',
                    );
            }
        },
        handleUndoLlmChange () {
            const { prevArtifact } = this.conversation;

            this.addMessage(
                {
                    conversationId: this.conversationId,
                    type: 'user',
                    content: this.$t('guidesConversationalAiPage.Undo last edit'),
                },
                null,
            );

            this.conversation.artifact = prevArtifact;
            if (prevArtifact) {
                this.processArtifact(prevArtifact);
            }
            this.conversation.prevArtifact = null;
            this.shouldShowLlmUndo = false;
        },
        handleContentUpdateByType ({ event, buildingBlock, step, stepIndex }) {
            const {
                type,
                content,
                needsRefresh,
                data: { dom },
            } = event;

            const text = content || getBlockContent({ type, event, turndownService: this.turndownService });

            setBlockContent({ buildingBlock, content: text, platform: this.platform });
            const isPoll = dom.getAttribute(POLL_ID_ATTRIBUTE);

            if (isPoll) {
                configurePollsGuide(this.localGuide);
            }

            if (step && stepIndex !== undefined) {
                step.dom = JSON.stringify(BuildingBlock.buildingBlocksToDom(step.buildingBlocks));
                const newArtifactStep = createArtifactStepsFromStep(step);
                this.conversation.artifact.steps.splice(stepIndex, 1, newArtifactStep);
            }

            if (needsRefresh) {
                this.refreshGuide();
            }
        },
        refreshGuide () {
            this.processArtifact(this.conversation.artifact);
        },
        deleteStep (index) {
            this.conversation.artifact.steps.splice(index, 1);
            this.localGuide.steps.splice(index, 1);
        },
        async copyStep (index) {
            const stepToCopy = this.localGuide.steps[index];
            const { themeId: stepThemeId } = stepToCopy.attributes;
            const theme = (stepThemeId
                ? this.themesOptions.find((theme) => theme.id === stepThemeId)
                : this.getDefaultThemeForAppId(this.parsedAppId)) ?? {};
            const insertIndex = index + 1;
            const newStep = await copyStepObject({
                guide: this.localGuide,
                stepToCopy,
                theme,
                isMobile: this.isMobile,
                isEmbed: this.isEmbed,
                rank: insertIndex,
            });
            newStep.key = makeConvoUiStepKey({ stepId: newStep.id, index: insertIndex, themeId: theme.id });

            // update the steps preview
            this.localGuide.steps.splice(insertIndex, 0, newStep);

            // update the artifact for the LLM
            const artifactStepClone = cloneDeep(this.conversation.artifact.steps[index]);
            this.conversation.artifact.steps.splice(insertIndex, 0, artifactStepClone);
        },
        async handleContentBlockUpdate (event) {
            const { editingId, stepIndex } = event;

            const { steps } = this.conversation.artifact;
            const artifactStep = steps[stepIndex];
            const step = this.localGuide.steps[stepIndex];
            const buildingBlock = BuildingBlock.findBlockByEditingId(step.buildingBlocks, editingId);

            this.handleContentUpdateByType({ event, artifactStep, buildingBlock, step, stepIndex });
        },
        trackAgent (eventType, { content, messageId, conversationId }) {
            const agentId = guidesAgentId();
            const payload = {
                agentId,
                conversationId,
                messageId,
                content,
            };
            trackAgentEvent(eventType, payload);
        },
    },
};

</script>
